import { useContext, useEffect, useRef, useState } from "react"
import Grid from "@mui/material/Grid2";
import { Box, IconButton, Slider } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { applyRiverFloatability, riverLayer } from "../../utilities/buffaloArcGisHelper";
import { rootContext } from "../../contexts/rootContext";

interface TimeSliderProps {
    open: boolean
    timeStrings: string[]
    polylineFloatabilityByTimeStringIndex: number[][]
}

export const TimeSlider = (props: TimeSliderProps) => {
    const context = useContext(rootContext)
    const { open, timeStrings, polylineFloatabilityByTimeStringIndex} = props
    const [selectedTimeIndex, setSelectedTimeIndex] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const updateScoresTimeoutId = useRef<NodeJS.Timeout | null>(null)
    const playTimeoutId = useRef<NodeJS.Timeout | null>(null)

    const PLAY_STEP_DELAY_MS = 400

    useEffect(() => {
        setSelectedTimeIndex(timeStrings.length-1)
    }, [timeStrings])

    const valueLabelFormat = (value: number) => {
        if (timeStrings.length === 0)
            return ""

        const splits = timeStrings[value].split(',')
        if (splits.length === 2)
            return <Grid container>
                <div style={{ fontWeight: "bold" }}>{splits[0]}</div>
                <div>{splits[1]}</div>
            </Grid>
        else
            return timeStrings[value]
    }

    const maxTimeIndex = timeStrings.length > 0 ? timeStrings.length-1 : 1

    const selectTimeIndex = (newValue: number) => {
        if(riverLayer === null ||
            newValue < 0 ||
            newValue >= polylineFloatabilityByTimeStringIndex.length
        )
            return

        if (updateScoresTimeoutId.current !== null)
            clearTimeout(updateScoresTimeoutId.current)

        updateScoresTimeoutId.current = setTimeout(async () => {
            await applyRiverFloatability(polylineFloatabilityByTimeStringIndex[newValue])
            setIsLoading(false)
        }, 500)

        setSelectedTimeIndex(newValue)
        setIsLoading(true)
    }

    const handleChange = (_event: Event, newValue: any) => {
        setIsPlaying(false)
        selectTimeIndex(newValue)
    }

    const step = (delta: number) => {
        setIsPlaying(false)
        const newValue = selectedTimeIndex + delta
        if (newValue < 0 || newValue > maxTimeIndex)
            return
        selectTimeIndex(newValue)
    }

    const togglePlay = () => {
        if (isPlaying) {
            setIsPlaying(false)
            return
        }

        if (selectedTimeIndex >= maxTimeIndex)
            selectTimeIndex(0)
        setIsPlaying(true)
    }

    useEffect(() => {
        if (!isPlaying || isLoading)
            return

        if (selectedTimeIndex >= maxTimeIndex) {
            setIsPlaying(false)
            return
        }

        playTimeoutId.current = setTimeout(() => {
            selectTimeIndex(selectedTimeIndex + 1)
        }, PLAY_STEP_DELAY_MS)

        return () => {
            if (playTimeoutId.current !== null)
                clearTimeout(playTimeoutId.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying, isLoading, selectedTimeIndex, maxTimeIndex])

    return (<Box
    sx={{
        position: "absolute",
        bottom: { xs: "20px", sm: "25px" },
        left: "10px",
        right: { xs: "10px", sm: "auto" },
        backgroundColor: "black",
        color: "white",
        paddingTop: "10px",
        paddingBottom: "10px",
        paddingLeft: { xs: "10px", sm: "20px" },
        paddingRight: { xs: "16px", sm: "30px" },
        borderRadius: "15px",
        width: { xs: "auto", sm: "400px" },
        maxWidth: "400px",
        userSelect: "none",
        display: open ? "flex" : "none",
        alignItems: "center",
        gap: { xs: 0, sm: 1 },
        boxSizing: "border-box",
        "& .MuiSlider-valueLabel": {
            fontSize: { xs: "0.7rem", sm: "0.875rem" }
        }
    }}>
            <Box
                sx={{
                    width: "30px",
                    height: "30px",
                    flexShrink: 0,
                    animation: "spin 1.75s linear infinite"
                }}
            >
                {isLoading ? "🪲" : ""}
            </Box>
            <IconButton
                onClick={togglePlay}
                disabled={timeStrings.length === 0}
                size="small"
                sx={{
                    color: context.theme.text.contrast4,
                    padding: { xs: "10px", sm: "5px" },
                    "&.Mui-disabled": { color: "#555" }
                }}
            >
                {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <IconButton
                onClick={() => step(-1)}
                disabled={selectedTimeIndex <= 0}
                size="small"
                sx={{
                    color: context.theme.text.contrast4,
                    padding: { xs: "10px", sm: "5px" },
                    "&.Mui-disabled": { color: "#555" }
                }}
            >
                <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Slider
                value={selectedTimeIndex}
                onChange={handleChange}
                valueLabelDisplay="on"
                valueLabelFormat={valueLabelFormat}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    "& .MuiSlider-thumb": {
                        backgroundColor: context.theme.text.contrast4
                    }
                }}
                step={1}
                marks
                min={0}
                max={maxTimeIndex}
            />
            <IconButton
                onClick={() => step(1)}
                disabled={selectedTimeIndex >= maxTimeIndex}
                size="small"
                sx={{
                    color: context.theme.text.contrast4,
                    padding: { xs: "10px", sm: "5px" },
                    "&.Mui-disabled": { color: "#555" }
                }}
            >
                <ChevronRightIcon fontSize="small" />
            </IconButton>
    </Box>)
}